<script setup>
import ErrorMessage from '@/components/shared/transform/ErrorMessage.vue'
import Help from '@/components/shared/core/Help.vue'

//Emits and props
defineProps({
  label: {
    type: String,
    default: '',
  },
  subLabel: {
    type: String,
    default: '',
  },
  isInline: {
    type: Boolean,
    default: false,
  },
  isList: {
    type: Boolean,
    default: false,
  },
  isMuted: {
    type: Boolean,
    default: false,
  },
  isOptional: {
    type: Boolean,
    default: false,
  },
  isRequired: {
    type: Boolean,
    default: false,
  },
  isHalfWidth: {
    type: Boolean,
    default: false,
  },
  help: {
    type: String,
    default: '',
  },
  error: {
    type: [String, Error, Object],
    default: '',
  },
  errors: {
    type: Array,
    default: () => [],
  },
  message: {
    type: String,
    default: '',
  },
  messages: {
    type: Array,
    default: () => [],
  },
})
</script>

<template>
  <div
    :class="{
      [$style.wrapper]: true,
      [$style.inline]: isInline,
      [$style.halfWidth]: isHalfWidth,
    }"
  >
    <span
      v-if="label"
      :class="{
        [$style.label]: true,
        [$style.muted]: isMuted,
        [$style.error]: errors.length > 0 || error,
      }"
    >
      <div :class="$style.labelContainer">
        <div :class="$style.labelWrapper">
          <span>
            {{ label }}
            <span
              v-if="isRequired"
              :class="$style.required"
            >*</span>
          </span>
          <small
            v-if="isOptional"
            :class="$style.optional"
          >(optional)</small>
          <Help
            v-if="help"
            :text="help"
          />
        </div>

        <small
          text-muted
          hide-empty
        >
          <slot name="subLabel">{{ subLabel }}</slot>
        </small>
      </div>
      <aside
        :class="$style.aside"
        hide-empty
      >
        <slot name="aside" />
      </aside>
    </span>
    <div
      :class="{
        [$style.input]: true,
        [$style.list]: isList,
      }"
    >
      <slot />
    </div>
    <slot name="underInput" />
    <div
      :class="$style.messages"
      hide-empty
      aria-live="polite"
    >
      <slot name="messages">
        <p v-if="message">
          {{ message }}
        </p>
        <ErrorMessage
          v-if="error"
          :error="error"
          is-block
        />
        <p
          v-for="(m, index) in messages"
          :key="index"
        >
          {{ m }}
        </p>
        <ErrorMessage
          v-for="(e, index) in errors"
          :key="e.$uid || index"
          :error="e"
          is-block
        />
      </slot>
    </div>
  </div>
</template>

<style module>
.wrapper {
  flex: 1;
  &:not(.inline):not(:first-child) {
    margin-top: 1.25rem;
  }
  &.halfWidth {
    width: 50%;
  }
}
.input {
  max-width: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  &.list {
    flex-direction: column;
    align-items: flex-start;
  }
}
.label {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: var(--font-size-label);
  font-weight: var(--font-weight-bold);
  @media (min-width: 768px) {
    flex-wrap: nowrap;
  }
  &.muted {
    color: var(--color-text-muted);
    font-weight: var(--font-weight-normal);
  }
  &.error {
    color: var(--color-text-danger);
  }
}
.optional {
  color: var(--color-text-muted);
}
.required {
  color: var(--color-text-danger);
}
.aside {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 1rem;
}
.messages {
  padding-top: 0.5rem;
  color: var(--color-text-muted);
  p:not(:first-child) {
    margin-top: 0.25rem;
  }
}
.labelContainer {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.labelWrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
</style>
